"use client";

import { useRef, useState } from "react";
import { uploadFileDirect, CreatorVoiceError } from "@/lib/creatorVoice/api";
import { useJob } from "@/lib/creatorVoice/useJob";
import { ErrorBox, JobProgress, SharedFreeTrialNote } from "@/components/creatorVoice/CreatorVoiceUI";
import UpgradeToPro from "@/components/platform/UpgradeToPro";
import Spinner from "@/components/Spinner";

type WordTiming = { word: string; start: number; end: number };

export default function SpeechToTextTool() {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { job, submitting, error, capped, run } = useJob();

  async function handleSubmit() {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const audioUrl = await uploadFileDirect(file);
      await run("stt", { audio_url: audioUrl });
    } catch (e) {
      setUploadError(e instanceof CreatorVoiceError ? e.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  const transcript = job?.result?.transcript as string | undefined;
  const wordTimings = job?.result?.word_timings as WordTiming[] | undefined;

  return (
    <div>
      <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Upload a WAV audio file</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-2 flex h-24 cursor-pointer items-center justify-center rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400"
      >
        {file ? file.name : "Click to choose a .wav file"}
        <input
          ref={inputRef}
          type="file"
          accept="audio/wav,audio/x-wav"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={uploading || submitting || !file}
          className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-50"
        >
          {uploading || submitting ? <Spinner className="h-4 w-4" /> : null}
          {uploading ? "Uploading…" : submitting ? "Transcribing…" : "Transcribe"}
        </button>
      </div>
      <SharedFreeTrialNote />

      {uploadError ? <ErrorBox message={uploadError} /> : null}
      {error ? <ErrorBox message={error} /> : null}
      {capped ? (
        <div className="mt-3">
          <UpgradeToPro />
        </div>
      ) : null}
      <JobProgress job={job} />

      {job?.status === "completed" && transcript ? (
        <div className="mt-4 rounded-xl border border-rose-200/80 bg-rose-50/60 p-4 dark:border-rose-800/60 dark:bg-rose-500/5">
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">Transcript</p>
          <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{transcript}</p>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(transcript)}
            className="mt-3 text-xs font-medium text-rose-600 hover:underline dark:text-rose-400"
          >
            Copy transcript
          </button>

          {wordTimings?.length ? (
            <details className="mt-4">
              <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-400">
                Word-level timing ({wordTimings.length} words)
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto text-xs text-slate-600 dark:text-slate-400">
                {wordTimings.map((w, i) => (
                  <span key={i} className="mr-2 inline-block">
                    {w.word} <span className="text-slate-400">({w.start.toFixed(2)}s)</span>
                  </span>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
